import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import api from '@/Modules/ETL/services/api'

export const usePipelineStore = defineStore('pipelines', () => {
  const pipelines = ref([])
  // Per-operation loading map — prevents concurrent ops from clobbering each other's state
  const loadingOps = reactive({})
  const loading = computed(() => Object.keys(loadingOps).length > 0)
  const error = ref(null)

  const isLoading = (op) => !!loadingOps[op]

  const createPipeline = async (pipelineData) => {
    try {
      loadingOps['createPipeline'] = true
      error.value = null

      const response = await api.createDataPipeline(pipelineData)
      const pipeline = response.data
      pipelines.value.push(pipeline)
      return pipeline
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error creating pipeline:', err)
      throw err
    } finally {
      delete loadingOps['createPipeline']
    }
  }

  const getPipeline = async (pipelineId) => {
    try {
      loadingOps['getPipeline'] = true
      error.value = null

      const response = await api.getPipeline(pipelineId)
      return response.data
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error fetching pipeline:', err)
      throw err
    } finally {
      delete loadingOps['getPipeline']
    }
  }

  const getSampleData = async (connectionId, endpoint) => {
    try {
      loadingOps['getSampleData'] = true
      error.value = null

      const response = await api.retrieveSampleDataFromEndpoint(connectionId, endpoint)
      return response.data
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error getting sample data:', err)
      throw err
    } finally {
      delete loadingOps['getSampleData']
    }
  }

  const fetchPipelines = async (status = null) => {
    try {
      loadingOps['fetchPipelines'] = true
      error.value = null

      const response = await api.getPipelines(status)
      pipelines.value = response.data
      return pipelines.value
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error fetching pipelines:', err)
      throw err
    } finally {
      delete loadingOps['fetchPipelines']
    }
  }

  const executePipeline = async (pipelineId) => {
    try {
      loadingOps['executePipeline'] = true
      error.value = null

      const response = await api.runPipeline(pipelineId)
      return response.data
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error executing pipeline:', err)
      throw err
    } finally {
      delete loadingOps['executePipeline']
    }
  }

  const updatePipeline = async (pipelineId, pipelineData) => {
    try {
      loadingOps['updatePipeline'] = true
      error.value = null

      const response = await api.updatePipeline(pipelineId, pipelineData)
      const updatedPipeline = response.data

      // Update in local state
      const index = pipelines.value.findIndex((p) => p.id === pipelineId)
      if (index !== -1) {
        pipelines.value[index] = updatedPipeline
      }

      return updatedPipeline
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error updating pipeline:', err)
      throw err
    } finally {
      delete loadingOps['updatePipeline']
    }
  }

  const deletePipeline = async (pipelineId) => {
    try {
      loadingOps['deletePipeline'] = true
      error.value = null

      await api.removePipeline(pipelineId)
      pipelines.value = pipelines.value.filter((p) => p.id !== pipelineId)
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error deleting pipeline:', err)
      throw err
    } finally {
      delete loadingOps['deletePipeline']
    }
  }

  const getPipelineExecutions = async (pipelineId) => {
    try {
      loadingOps['getPipelineExecutions'] = true
      error.value = null

      const response = await api.getPipelineExecutions(pipelineId)
      return response.data
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error fetching pipeline executions:', err)
      throw err
    } finally {
      delete loadingOps['getPipelineExecutions']
    }
  }

  const getExecutionDetails = async (executionId) => {
    try {
      loadingOps['getExecutionDetails'] = true
      error.value = null

      const response = await api.getExecutionDetails(executionId)
      return response.data
    } catch (err) {
      error.value = err.errorMessage || err.message || 'Unknown error'
      console.error('Error fetching execution details:', err)
      throw err
    } finally {
      delete loadingOps['getExecutionDetails']
    }
  }

  const clearError = () => {
    error.value = null
  }

  return {
    pipelines,
    loading,
    error,
    isLoading,
    createPipeline,
    getPipeline,
    getSampleData,
    fetchPipelines,
    executePipeline,
    updatePipeline,
    deletePipeline,
    getPipelineExecutions,
    getExecutionDetails,
    clearError,
  }
})
